import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import api from "../../contents/api/api";
import { toast } from "react-toastify";
import styles from "./cssmodules/BulkPermissionAssigner.module.css";
import { CircularProgress } from "@mui/material";

const BulkPermissionAssigner = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [allPermissions, setAllPermissions] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    failed: 0,
    skipped: 0,
  });

  useEffect(() => {
    // Get user role info from localStorage
    const storedRoleId = localStorage.getItem("loggedInUserRoleId");
    const storedRoleName = localStorage.getItem("loggedInUserRoleName");

    setRoleId(storedRoleId);
    setRoleName(storedRoleName);

    // Fetch all permissions and categories
    fetchPermissionsAndCategories();
  }, []);

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { message, type, timestamp }]);
  };

  const fetchPermissionsAndCategories = async () => {
    try {
      addLog("Fetching all permissions and categories...", "info");

      // Fetch all categories
      const categoriesResponse = await api.get("/RolePermission/Permission-category");
      setAllCategories(categoriesResponse.data);
      addLog(`Loaded ${categoriesResponse.data.length} permission categories`, "success");

      // Build all possible permission combinations
      // Each category can have: View, Create, Update, Delete, Print, and category-specific actions
      const commonActions = ["View", "Create", "Update", "Delete", "Print"];
      const specialActions = {
        "AccessRequests": ["Approve", "Reject"],
        "MyPayments": ["Pay"],
        "Payments": ["Pay"],
        "Membership": ["Renew"],
        "Reports": ["Generate"],
      };

      const allPermissionCombos = [];
      categoriesResponse.data.forEach(category => {
        // Add common actions
        commonActions.forEach(action => {
          allPermissionCombos.push({
            categoryId: category.id,
            categoryName: category.name,
            permissionId: action,
            permissionName: action,
          });
        });

        // Add special actions for specific categories
        if (specialActions[category.id]) {
          specialActions[category.id].forEach(action => {
            allPermissionCombos.push({
              categoryId: category.id,
              categoryName: category.name,
              permissionId: action,
              permissionName: action,
            });
          });
        }
      });

      setAllPermissions(allPermissionCombos);
      addLog(`Generated ${allPermissionCombos.length} permission combinations`, "success");

      console.log("Sample permission combo:", allPermissionCombos[0]);
      console.log("Sample category:", categoriesResponse.data[0]);

      setFetching(false);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      addLog("Failed to fetch permissions and categories", "error");
      toast.error("Failed to load permissions data");
      setFetching(false);
    }
  };

  const assignAllPermissions = async () => {
    if (!roleId) {
      toast.error("Role ID not found. Please log in again.");
      return;
    }

    if (!window.confirm(
      `Are you sure you want to assign ALL ${allPermissions.length} permissions to your role "${roleName}"?\n\nThis will give you full system access.`
    )) {
      return;
    }

    setLoading(true);
    setProgress(0);
    setStats({ total: 0, assigned: 0, failed: 0, skipped: 0 });
    setLogs([]);
    addLog("Starting bulk permission assignment...", "info");

    const userId = localStorage.getItem("loggedInUserId");
    let successCount = 0;
    let failCount = 0;
    let skipCount = 0;

    for (let i = 0; i < allPermissions.length; i++) {
      const permission = allPermissions[i];

      try {
        const payload = {
          permissionCategoryId: permission.categoryId,
          permissionId: permission.permissionId,
          roleId: parseInt(roleId),
          userLogId: parseInt(userId),
          updateLogId: parseInt(userId),
        };

        addLog(
          `Assigning: ${permission.categoryName} - ${permission.permissionName}`,
          "info"
        );

        await api.post("/RolePermission/assign-permission-role", payload);

        successCount++;
        addLog(
          `✓ Assigned: ${permission.categoryName} - ${permission.permissionName}`,
          "success"
        );
      } catch (error) {
        if (error.response?.status === 400) {
          // Permission already exists, count as skipped
          skipCount++;
          addLog(
            `○ Already exists: ${permission.permissionName}`,
            "warning"
          );
        } else {
          failCount++;
          addLog(
            `✗ Failed: ${permission.permissionName} - ${error.response?.data?.message || error.message}`,
            "error"
          );
        }
      }

      // Update progress
      const progressPercent = ((i + 1) / allPermissions.length) * 100;
      setProgress(progressPercent);
      setStats({
        total: i + 1,
        assigned: successCount,
        failed: failCount,
        skipped: skipCount,
      });

      // Small delay to prevent overwhelming the server
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    setLoading(false);
    addLog("Bulk assignment completed!", "success");

    toast.success(
      `Assigned ${successCount} permissions successfully! (${skipCount} already existed, ${failCount} failed)`
    );

    // Suggest page reload to refresh permissions
    if (successCount > 0) {
      setTimeout(() => {
        if (window.confirm("Permissions updated! Would you like to reload the page to apply changes?")) {
          window.location.reload();
        }
      }, 2000);
    }
  };

  if (fetching) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
          <p>Loading permissions data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Bulk Permission Assigner</h1>
        <p className={styles.subtitle}>
          Assign all available permissions to your role
        </p>
      </div>

      {/* Role Info */}
      <div className={styles.roleInfo}>
        <h2 className={styles.sectionTitle}>Your Role Information</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.label}>Role Name:</span>
            <span className={styles.value}>{roleName || "N/A"}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>Role ID:</span>
            <span className={styles.value}>{roleId || "N/A"}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>Total Permissions Available:</span>
            <span className={styles.value}>{allPermissions.length}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>Permission Categories:</span>
            <span className={styles.value}>{allCategories.length}</span>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className={styles.warningBox}>
        <h3>⚠️ Important Warning</h3>
        <p>
          This tool will assign <strong>ALL {allPermissions.length} permissions</strong> to your role.
          This will give you complete system access.
        </p>
        <ul>
          <li>Only use this if you are a system administrator</li>
          <li>This action cannot be easily undone</li>
          <li>Page will reload after completion to apply changes</li>
        </ul>
      </div>

      {/* Action Button */}
      <div className={styles.actionSection}>
        <button
          className={styles.assignButton}
          onClick={assignAllPermissions}
          disabled={loading || !roleId}
        >
          {loading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: "10px" }} />
              Assigning Permissions... ({Math.round(progress)}%)
            </>
          ) : (
            `Assign All ${allPermissions.length} Permissions`
          )}
        </button>
      </div>

      {/* Progress Stats */}
      {loading && (
        <div className={styles.statsContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className={styles.statsGrid}>
            <div className={styles.statBox}>
              <span className={styles.statValue}>{stats.total}</span>
              <span className={styles.statLabel}>Processed</span>
            </div>
            <div className={styles.statBox} style={{ borderColor: "#228506" }}>
              <span className={styles.statValue} style={{ color: "#228506" }}>
                {stats.assigned}
              </span>
              <span className={styles.statLabel}>Assigned</span>
            </div>
            <div className={styles.statBox} style={{ borderColor: "#F19104" }}>
              <span className={styles.statValue} style={{ color: "#F19104" }}>
                {stats.skipped}
              </span>
              <span className={styles.statLabel}>Skipped</span>
            </div>
            <div className={styles.statBox} style={{ borderColor: "#fd5361" }}>
              <span className={styles.statValue} style={{ color: "#fd5361" }}>
                {stats.failed}
              </span>
              <span className={styles.statLabel}>Failed</span>
            </div>
          </div>
        </div>
      )}

      {/* Logs */}
      {logs.length > 0 && (
        <div className={styles.logsSection}>
          <h3 className={styles.logsTitle}>Activity Log</h3>
          <div className={styles.logsContainer}>
            {logs.map((log, index) => (
              <div
                key={index}
                className={`${styles.logEntry} ${styles[log.type]}`}
              >
                <span className={styles.timestamp}>[{log.timestamp}]</span>
                <span className={styles.message}>{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkPermissionAssigner;
